import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import EnglishDatePicker from '../../components/EnglishDatePicker/EnglishDatePicker';
import Sidebar from '../../components/Sidebar';
import '../Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://metro4.dcairo.com/api';

const BreakingResults = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPour, setSelectedPour] = useState(null);
  const [pours, setPours] = useState([]);
  const [selectedAge, setSelectedAge] = useState('');
  const [testingDate, setTestingDate] = useState(new Date().toISOString().split('T')[0]);
  
  const [tables, setTables] = useState([
    {
      id: 1,
      results: Array(8).fill(null).map((_, i) => ({
        group: i + 1,
        cube1: { weight: '', density: '', load: '', strength: '' },
        cube2: { weight: '', density: '', load: '', strength: '' },
        groupAverage: 0,
        overallAverage: 0,
        note: ''
      }))
    }
  ]);
  
  const [signatures, setSignatures] = useState({
    ocLab: { name: '', date: '' },
    thirdParty: { name: '', date: '' },
    noce: { name: '', date: '' }
  });
  
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  useEffect(() => {
    fetchPours();
  }, []);

  const fetchPours = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/pours?status=approved`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPours(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching pours:', error);
      if (error.response?.status === 401) {
        toast.error('انتهت الجلسة');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePourSelect = (pourId) => {
    const pour = pours.find(p => p._id === pourId);
    setSelectedPour(pour);
  };

  const addNewTable = () => {
    const newTable = {
      id: tables.length + 1,
      results: Array(8).fill(null).map((_, i) => ({
        group: i + 1,
        cube1: { weight: '', density: '', load: '', strength: '' },
        cube2: { weight: '', density: '', load: '', strength: '' },
        groupAverage: 0,
        overallAverage: 0,
        note: ''
      }))
    };
    setTables([...tables, newTable]);
    toast.success('تم إضافة جدول جديد');
  };

  const removeTable = (tableId) => {
    if (tables.length === 1) {
      toast.error('يجب أن يكون هناك جدول واحد على الأقل');
      return;
    }
    setTables(tables.filter(t => t.id !== tableId));
    toast.success('تم حذف الجدول');
  };

  const calculateStrength = (load) => {
    if (!load || load === '') return '';
    const loadNum = parseFloat(load);
    const area = 150 * 150;
    const strength = (loadNum * 1000) / area;
    return strength.toFixed(2);
  };

  const calculateDensity = (weight) => {
    if (!weight || weight === '') return '';
    const weightNum = parseFloat(weight);
    const volume = 150 * 150 * 150;
    const density = (weightNum * 1000000) / volume;
    return density.toFixed(2);
  };

  const handleCubeChange = (tableIndex, groupIndex, cubeNumber, field, value) => {
    const newTables = [...tables];
    const cube = cubeNumber === 1 ? 'cube1' : 'cube2';
    
    newTables[tableIndex].results[groupIndex][cube][field] = value;
    
    if (field === 'weight') {
      newTables[tableIndex].results[groupIndex][cube].density = calculateDensity(value);
    }
    
    if (field === 'load') {
      newTables[tableIndex].results[groupIndex][cube].strength = calculateStrength(value);
    }
    
    const strength1 = parseFloat(newTables[tableIndex].results[groupIndex].cube1.strength) || 0;
    const strength2 = parseFloat(newTables[tableIndex].results[groupIndex].cube2.strength) || 0;
    newTables[tableIndex].results[groupIndex].groupAverage = ((strength1 + strength2) / 2).toFixed(2);
    
    const validAverages = newTables[tableIndex].results
      .map(r => parseFloat(r.groupAverage))
      .filter(avg => avg > 0);
    const overallAvg = validAverages.length > 0 
      ? (validAverages.reduce((a, b) => a + b, 0) / validAverages.length).toFixed(2)
      : 0;
    
    newTables[tableIndex].results.forEach(r => r.overallAverage = overallAvg);
    
    setTables(newTables);
  };

  const handleNoteChange = (tableIndex, groupIndex, value) => {
    const newTables = [...tables];
    newTables[tableIndex].results[groupIndex].note = value;
    setTables(newTables);
  };

  const handleSignatureChange = (type, field, value) => {
    setSignatures(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const handleSaveResults = async () => {
    if (!selectedPour) {
      toast.error('اختر الصبة أولاً');
      return;
    }

    if (!selectedAge) {
      toast.error('اختر عمر الاختبار');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const allResults = tables.flatMap(table => table.results);
      
      const payload = {
        age: selectedAge,
        testingDate: testingDate,
        results: allResults,
        signatures: signatures
      };

      const response = await axios.post(
        `${API_URL}/pours/${selectedPour._id}/breaking-results`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('تم حفظ النتائج بنجاح');
      }
    } catch (error) {
      console.error('Error saving results:', error);
      toast.error(error.response?.data?.message || 'فشل حفظ النتائج');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleScreenshot = async () => {
    const element = document.getElementById('printable-area');
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false
    });
    
    const link = document.createElement('a');
    link.download = `breaking-results-${selectedPour?.station || 'report'}.png`;
    link.href = canvas.toDataURL();
    link.click();
    toast.success('تم حفظ الصورة');
  };

  const handleSavePDF = async () => {
    const element = document.getElementById('printable-area');
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    pdf.save(`breaking-results-${selectedPour?.station || 'report'}.pdf`);
    toast.success('تم حفظ PDF');
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB');
  };

  const totalCubes = tables.length * 16;

  return (
    <div className="dashboard-layout">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <div className="dashboard-content">
        <div className="dashboard-header no-print">
          <button className="menu-toggle" onClick={toggleSidebar}>
            ☰
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => navigate('/cubes')}
              style={{
                background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '700',
                transition: 'all 0.3s',
                boxShadow: '0 4px 12px rgba(100, 116, 139, 0.3)'
              }}
            >
              ← رجوع للمكعبات
            </button>
            <h1 style={{ margin: 0 }}>📊 نتائج التكسير</h1>
          </div>
        </div>

        <div className="dashboard-main">
		<style>{`
            @media print {
              .no-print {
                display: none !important;
              }
              .dashboard-content {
                padding: 0 !important;
                margin: 0 !important;
              }
              .printable-area {
                page-break-after: always;
              }
              body {
                margin: 0;
                padding: 0;
              }
            }

            .selection-container {
              background: white;
              padding: 20px;
              border-radius: 12px;
              margin-bottom: 20px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }

            .selection-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
              margin-bottom: 15px;
            }

            .selection-field {
              display: flex;
              flex-direction: column;
              gap: 5px;
            }

            .selection-label {
              font-size: 13px;
              font-weight: 700;
              color: #64748b;
            }

            .selection-input {
              padding: 10px;
              border: 2px solid #e2e8f0;
              border-radius: 8px;
              font-size: 14px;
            }

            .action-buttons {
              display: flex;
              gap: 10px;
              flex-wrap: wrap;
              margin-bottom: 20px;
            }

            .action-btn {
              padding: 12px 24px;
              border: none;
              border-radius: 8px;
              font-size: 14px;
              font-weight: 700;
              cursor: pointer;
              transition: all 0.3s;
              display: flex;
              align-items: center;
              gap: 8px;
            }

            .btn-save {
              background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
              color: white;
            }

            .btn-print {
              background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
              color: white;
            }

            .btn-screenshot {
              background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
              color: white;
            }

            .btn-pdf {
              background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
              color: white;
            }

            .btn-add-table {
              background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
              color: white;
            }

            .action-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(0,0,0,0.2);
            }

            .printable-area {
              background: white;
              padding: 20mm;
              max-width: 210mm;
              margin: 0 auto;
            }

            .lab-header {
              text-align: center;
              margin-bottom: 10px;
              border-bottom: 3px solid #000;
              padding-bottom: 10px;
            }

            .lab-header img {
              width: 100%;
              max-width: 800px;
              height: auto;
            }

            .form-info {
              border: 2px solid #000;
              margin-bottom: 10px;
            }

            .info-row {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              border-bottom: 2px solid #000;
            }

            .info-row:last-child {
              border-bottom: none;
            }

            .info-cell {
              padding: 8px 10px;
              border-right: 2px solid #000;
              display: flex;
              align-items: center;
              min-height: 35px;
            }

            .info-cell:last-child {
              border-right: none;
            }

            .info-label {
              font-weight: 700;
              font-size: 12px;
              margin-right: 5px;
            }

            .info-value {
              font-size: 12px;
              font-weight: 600;
            }

            .results-table {
              width: 100%;
              border-collapse: collapse;
              border: 2px solid #000;
              margin-bottom: 10px;
            }

            .results-table th,
            .results-table td {
              border: 1px solid #000;
              padding: 4px 2px;
              text-align: center;
              font-size: 10px;
            }

            .results-table th {
              background: #f0f0f0;
              font-weight: 700;
              font-size: 9px;
            }

            .group-cell {
              font-weight: 700;
              background: #f8f8f8;
            }

            .input-cell {
              padding: 2px;
            }

            .table-input {
              width: 100%;
              border: none;
              text-align: center;
              font-size: 10px;
              padding: 2px;
            }

            .table-input:focus {
              outline: 1px solid #3b82f6;
            }

            .readonly-input {
              background: #f8f8f8;
            }

            .signatures-section {
              border: 2px solid #000;
              margin-top: 10px;
            }

            .signatures-grid {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
            }

            .signature-col {
              border-right: 2px solid #000;
              text-align: center;
            }

            .signature-col:last-child {
              border-right: none;
            }

            .signature-header {
              padding: 8px;
              font-weight: 700;
              font-size: 11px;
              border-bottom: 2px solid #000;
              background: #f0f0f0;
            }

            .signature-row {
              padding: 8px;
              border-bottom: 1px solid #000;
              min-height: 30px;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .signature-row:last-child {
              border-bottom: none;
            }

            .signature-label {
              font-size: 10px;
              font-weight: 700;
              margin-bottom: 3px;
            }

            .signature-input {
              width: 90%;
              border: none;
              border-bottom: 1px solid #ccc;
              text-align: center;
              font-size: 10px;
              padding: 2px;
            }

            .table-number {
              text-align: center;
              font-weight: 700;
              margin: 15px 0 5px 0;
              font-size: 14px;
            }

            .remove-table-btn {
              background: #dc2626;
              color: white;
              border: none;
              padding: 5px 15px;
              border-radius: 5px;
              cursor: pointer;
              font-size: 12px;
              margin: 5px auto;
              display: block;
            }

            @media screen and (max-width: 768px) {
              .printable-area {
                padding: 10px;
              }
              
              .results-table th,
              .results-table td {
                font-size: 8px;
                padding: 2px 1px;
              }
            }
          `}</style>

          <div className="no-print">
            <div className="selection-container">
              <div className="selection-grid">
                <div className="selection-field">
                  <label className="selection-label">Select Pour</label>
                  <select 
                    className="selection-input"
                    onChange={(e) => handlePourSelect(e.target.value)}
                    value={selectedPour?._id || ''}
                  >
                    <option value="">اختر الصبة</option>
                    {pours.map(pour => (
                      <option key={pour._id} value={pour._id}>
                        {pour.station} - {pour.location} - {formatDate(pour.castingDate)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="selection-field">
                  <label className="selection-label">Test Age</label>
                  <select 
                    className="selection-input"
                    onChange={(e) => setSelectedAge(e.target.value)}
                    value={selectedAge}
                    disabled={!selectedPour}
                  >
                    <option value="">اختر عمر الاختبار</option>
                    <option value="7">7 days</option>
                    <option value="14">14 days</option>
                    <option value="28">28 days</option>
                    <option value="56">56 days</option>
                  </select>
                </div>

                <div className="selection-field">
                  <label className="selection-label">Testing Date</label>
                  <EnglishDatePicker
                    selected={testingDate ? new Date(testingDate) : null}
                    onChange={(date) => setTestingDate(date ? date.toISOString().split('T')[0] : '')}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select Date"
                  />
                </div>
              </div>

              <div className="action-buttons">
                <button className="action-btn btn-save" onClick={handleSaveResults} disabled={!selectedPour || !selectedAge}>
                  <span>💾</span> حفظ النتائج
                </button>
                <button className="action-btn btn-add-table" onClick={addNewTable}>
                  <span>➕</span> إضافة جدول آخر
                </button>
                <button className="action-btn btn-print" onClick={handlePrint}>
                  <span>🖨️</span> طباعة
                </button>
                <button className="action-btn btn-screenshot" onClick={handleScreenshot}>
                  <span>📸</span> Screenshot
                </button>
                <button className="action-btn btn-pdf" onClick={handleSavePDF}>
                  <span>📄</span> Save PDF
                </button>
              </div>
            </div>
          </div>
		  {selectedPour && (
            <div id="printable-area" className="printable-area">
              <div className="lab-header">
                <img src="/images/lab-header.jpg" alt="Laboratory Header" />
              </div>

              <div className="form-info">
                <div className="info-row">
                  <div className="info-cell">
                    <span className="info-label">Casting Date:</span>
                    <span className="info-value">{formatDate(selectedPour.castingDate)}</span>
                  </div>
                  <div className="info-cell">
                    <span className="info-label">Station:</span>
                    <span className="info-value">{selectedPour.station}</span>
                  </div>
                  <div className="info-cell">
                    <span className="info-label">Batch Plant:</span>
                    <span className="info-value">{selectedPour.batchPlant}</span>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-cell">
                    <span className="info-label">Testing Date:</span>
                    <span className="info-value">{testingDate}</span>
                  </div>
                  <div className="info-cell">
                    <span className="info-label">Element:</span>
                    <span className="info-value">{selectedPour.location}</span>
                  </div>
                  <div className="info-cell">
                    <span className="info-label">Age:</span>
                    <span className="info-value">{selectedAge} days</span>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-cell">
                    <span className="info-label">Mix grade:</span>
                    <span className="info-value">{selectedPour.mix}</span>
                  </div>
                  <div className="info-cell" style={{ gridColumn: 'span 2' }}>
                    <span className="info-label">Total Number of Cubes:</span>
                    <span className="info-value">{totalCubes}</span>
                  </div>
                </div>
              </div>

              {tables.map((table, tableIndex) => (
                <div key={table.id}>
                  {tables.length > 1 && (
                    <>
                      <div className="table-number">Table {tableIndex + 1}</div>
                      <button 
                        className="remove-table-btn no-print"
                        onClick={() => removeTable(table.id)}
                      >
                        حذف هذا الجدول
                      </button>
                    </>
                  )}
                  
                  <table className="results-table">
                    <thead>
                      <tr>
                        <th rowSpan="2">SN</th>
                        <th rowSpan="2">Group</th>
                        <th rowSpan="2">Weight<br/>(gm)</th>
                        <th rowSpan="2">Density<br/>(gm/cm³)</th>
                        <th rowSpan="2">Load<br/>(KN)</th>
                        <th rowSpan="2">Strength<br/>(MPa)</th>
                        <th colSpan="2">Average strength</th>
                        <th rowSpan="2">Note</th>
                      </tr>
                      <tr>
                        <th>(MPa)</th>
                        <th>(MPa)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {table.results.map((result, groupIndex) => (
                        <React.Fragment key={groupIndex}>
                          <tr>
                            <td>{tableIndex * 16 + groupIndex * 2 + 1}</td>
                            <td rowSpan="2" className="group-cell">G{result.group}</td>
                            <td className="input-cell">
                              <input
                                type="number"
                                className="table-input"
                                value={result.cube1.weight}
                                onChange={(e) => handleCubeChange(tableIndex, groupIndex, 1, 'weight', e.target.value)}
                              />
                            </td>
                            <td className="input-cell">
                              <input
                                type="text"
                                className="table-input readonly-input"
                                value={result.cube1.density}
                                readOnly
                              />
                            </td>
                            <td className="input-cell">
                              <input
                                type="number"
                                className="table-input"
                                value={result.cube1.load}
                                onChange={(e) => handleCubeChange(tableIndex, groupIndex, 1, 'load', e.target.value)}
                              />
                            </td>
                            <td className="input-cell">
                              <input
                                type="text"
                                className="table-input readonly-input"
                                value={result.cube1.strength}
                                readOnly
                              />
                            </td>
                            <td rowSpan="2" style={{ fontWeight: '700' }}>{result.groupAverage || '-'}</td>
                            <td rowSpan="2" style={{ fontWeight: '700' }}>{result.overallAverage || '-'}</td>
                            <td rowSpan="2" className="input-cell">
                              <input
                                type="text"
                                className="table-input"
                                value={result.note}
                                onChange={(e) => handleNoteChange(tableIndex, groupIndex, e.target.value)}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td>{tableIndex * 16 + groupIndex * 2 + 2}</td>
                            <td className="input-cell">
                              <input
                                type="number"
                                className="table-input"
                                value={result.cube2.weight}
                                onChange={(e) => handleCubeChange(tableIndex, groupIndex, 2, 'weight', e.target.value)}
                              />
                            </td>
                            <td className="input-cell">
                              <input
                                type="text"
                                className="table-input readonly-input"
                                value={result.cube2.density}
                                readOnly
                              />
                            </td>
                            <td className="input-cell">
                              <input
                                type="number"
                                className="table-input"
                                value={result.cube2.load}
                                onChange={(e) => handleCubeChange(tableIndex, groupIndex, 2, 'load', e.target.value)}
                              />
                            </td>
                            <td className="input-cell">
                              <input
                                type="text"
                                className="table-input readonly-input"
                                value={result.cube2.strength}
                                readOnly
                              />
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}

              <div className="signatures-section">
                <div className="signatures-grid">
                  <div className="signature-col">
                    <div className="signature-header">OC Lab. Engineer</div>
                    <div className="signature-row">
                      <div>
                        <div className="signature-label">Name</div>
                        <input 
                          type="text" 
                          className="signature-input"
                          value={signatures.ocLab.name}
                          onChange={(e) => handleSignatureChange('ocLab', 'name', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="signature-row">
                      <div className="signature-label">Signature</div>
                    </div>
                    <div className="signature-row">
                      <div>
                        <div className="signature-label">Date</div>
                        <EnglishDatePicker
                          selected={signatures.ocLab.date ? new Date(signatures.ocLab.date) : null}
                          onChange={(date) => handleSignatureChange('ocLab', 'date', date ? date.toISOString().split('T')[0] : '')}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="Select Date"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="signature-col">
                    <div className="signature-header">4TL (Third Party)</div>
                    <div className="signature-row">
                      <div>
                        <div className="signature-label">Name</div>
                        <input 
                          type="text" 
                          className="signature-input"
                          value={signatures.thirdParty.name}
                          onChange={(e) => handleSignatureChange('thirdParty', 'name', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="signature-row">
                      <div className="signature-label">Signature</div>
                    </div>
                    <div className="signature-row">
                      <div>
                        <div className="signature-label">Date</div>
                        <EnglishDatePicker
                          selected={signatures.thirdParty.date ? new Date(signatures.thirdParty.date) : null}
                          onChange={(date) => handleSignatureChange('thirdParty', 'date', date ? date.toISOString().split('T')[0] : '')}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="Select Date"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="signature-col">
                    <div className="signature-header">NOCE/NAT</div>
                    <div className="signature-row">
                      <div>
                        <div className="signature-label">Name</div>
                        <input 
                          type="text" 
                          className="signature-input"
                          value={signatures.noce.name}
                          onChange={(e) => handleSignatureChange('noce', 'name', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="signature-row">
                      <div className="signature-label">Signature</div>
                    </div>
                    <div className="signature-row">
                      <div>
                        <div className="signature-label">Date</div>
                        <EnglishDatePicker
                          selected={signatures.noce.date ? new Date(signatures.noce.date) : null}
                          onChange={(date) => handleSignatureChange('noce', 'date', date ? date.toISOString().split('T')[0] : '')}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="Select Date"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!selectedPour && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
              <div style={{ fontSize: '60px', marginBottom: '20px' }}>🔍</div>
              <h3 style={{ fontSize: '20px', color: '#64748b' }}>اختر صبة للبدء</h3>
              <p>اختر الصبة وعمر الاختبار من القائمة أعلاه</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BreakingResults;